import { db } from "@/db/db";
import { Address } from "@/types/address.types";

export class AddressRepository {
  async findAll(): Promise<Address[]> {
    const addresses = await db.addresses.orderBy("createdAt").reverse().toArray();
    return addresses.map(addr => ({
      ...addr,
      createdAt: this.serializeDate(addr.createdAt),
      updatedAt: this.serializeDate(addr.updatedAt)
    }));
  }

  async findById(id: string): Promise<Address | undefined> {
    const address = await db.addresses.get(id);
    if (address) {
      return {
        ...address,
        createdAt: this.serializeDate(address.createdAt),
        updatedAt: this.serializeDate(address.updatedAt)
      };
    }
    return undefined;
  }

  async findByCall(call: string): Promise<Address | undefined> {
    const address = await db.addresses.where("call").equals(call).first();
    if (address) {
      return {
        ...address,
        createdAt: this.serializeDate(address.createdAt),
        updatedAt: this.serializeDate(address.updatedAt)
      };
    }
    return undefined;
  }

  async findByCalls(calls: string[]): Promise<Address[]> {
    const addresses = await db.addresses.where("call").anyOf(calls).toArray();
    return addresses.map(addr => ({
      ...addr,
      createdAt: this.serializeDate(addr.createdAt),
      updatedAt: this.serializeDate(addr.updatedAt)
    }));
  }

  /**
   * 将 Date 对象序列化为 ISO 字符串
   */
  private serializeDate(date: unknown): string {
    if (date instanceof Date) {
      return date.toISOString();
    }
    return String(date);
  }

  async create(address: Omit<Address, "id" | "createdAt" | "updatedAt">): Promise<Address> {
    const now = new Date().toISOString();
    const newAddress: Address = {
      id: this.generateId(),
      createdAt: now,
      updatedAt: now,
      ...address,
    };
    await db.addresses.add(newAddress);
    return newAddress;
  }

  async update(id: string, updates: Partial<Address>): Promise<void> {
    await db.addresses.update(id, { ...updates, updatedAt: new Date().toISOString() });
  }

  async delete(id: string): Promise<void> {
    await db.addresses.delete(id);
  }

  async deleteAll(): Promise<void> {
    await db.addresses.clear();
  }

  async bulkCreate(addresses: Omit<Address, "id" | "createdAt" | "updatedAt">[]): Promise<void> {
    const now = new Date().toISOString();
    const newAddresses: Address[] = addresses.map((address) => ({
      id: this.generateId(),
      createdAt: now,
      updatedAt: now,
      ...address,
    }));
    await db.addresses.bulkPut(newAddresses);
  }

  private generateId(): string {
    return `address_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const addressRepository = new AddressRepository();
